/* eslint-disable complexity */
import type { MutableRefObject, RefObject } from 'react';
import { createRef, useMemo, useRef } from 'react';
import { ITEM_TYPES, FOOTER_DS_ID } from '../constants/index.js';
import type { DSLeftNavigationT } from '../react-desc-prop-types.js';

const notFocuseableItems = [ITEM_TYPES.LEFT_NAV_ITEM_SEPARATOR, ITEM_TYPES.LEFT_NAV_ITEM_SECTION];

// Walks the array of items
// Drilldowns are tree-like structures
const walkItems = (
  items: DSLeftNavigationT.GenericItemItemProps[],
  openedDrilldowns: string[],
  callback: (dsId: string) => void,
) => {
  items.forEach((item) => {
    if (typeof item.type === 'string' && item.dsId) {
      // Out of the box item
      if (notFocuseableItems.includes(item.type)) return; // not focuseable

      callback(item.dsId);

      // If it's an opened uncontrolled drilldown
      if (item.type === ITEM_TYPES.LEFT_NAV_ITEM_UNCONTROLLED_DRILLDOWN && openedDrilldowns.includes(item.dsId))
        walkItems(item.itemOpts?.items ?? [], openedDrilldowns, callback);

      // If it's an opened controlled drilldown
      if (item.type === ITEM_TYPES.LEFT_NAV_ITEM_CONTROLLED_DRILLDOWN && item.itemOpts?.isOpened)
        walkItems(item.itemOpts?.items ?? [], openedDrilldowns, callback);
    } else if (item.dsId) {
      // Custom item
      if (!item.itemOpts?.focuseable) return; // not focuseable specified
      callback(item.dsId);
    }
  });
};

export const useLeftNavItems = (props: {
  headerItem?: DSLeftNavigationT.GenericItemItemProps;
  bodyHeaderItem?: DSLeftNavigationT.GenericItemItemProps;
  items?: DSLeftNavigationT.GenericItemItemProps[];
  openedDrilldowns: string[];
}): {
  visibleItems: string[];
  visibleItemsRefs: MutableRefObject<Record<string, RefObject<HTMLDivElement>>>;
} => {
  const { headerItem, bodyHeaderItem, items: bodyItems, openedDrilldowns } = props;

  // Place the header and body header items into the array of items
  const items = useMemo(() => {
    if (!bodyItems) return [];
    const itemsArray: DSLeftNavigationT.GenericItemItemProps[] = [...bodyItems];
    if (bodyHeaderItem) itemsArray.unshift(bodyHeaderItem);
    if (headerItem) itemsArray.unshift(headerItem);
    return itemsArray;
  }, [headerItem, bodyHeaderItem, bodyItems]);

  const visibleItemsRefs = useRef<Record<string, RefObject<HTMLDivElement>>>({});

  const visibleItems: string[] = useMemo(() => {
    const visibleItemsArray: string[] = [];
    walkItems(items, openedDrilldowns, (dsId: string) => {
      visibleItemsArray.push(dsId);
    });
    visibleItemsArray.push(FOOTER_DS_ID);
    return visibleItemsArray;
  }, [items, openedDrilldowns]);

  visibleItems.forEach((dsId) => {
    if (!(dsId in visibleItemsRefs.current)) visibleItemsRefs.current[dsId] = createRef();
  });

  return { visibleItems, visibleItemsRefs };
};

export default useLeftNavItems;
